import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { notifyError } from '@/core/notify/notify';
import { selectClient } from '@/core/selectedClient';
import { workspaceService } from '@/core/services/workspace';
import type {
  DashboardOverduePeriod,
  DashboardSummary,
  DashboardTileKey,
  DashboardTileList,
  DashboardTileRow,
  UserLogRow,
  WorkspaceOption,
} from '@/core/models/workspace.models';
import { Button } from '@/kit/Button';
import { DashboardToolbar, DashboardZones } from '@/kit/dashboard/DashboardLayout';
import { useDashboardLayout, type WidgetDef } from '@/kit/dashboard/useDashboardLayout';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { Paginator } from '@/kit/Paginator';
import { accountsLink } from './accountsFilters';
import { MatSelectField, type MatSelectOption } from './MatSelectField';
import { AccountsStatusWidget, MonthlySubmittedWidget, useSubmissionSummary } from './SubmissionCharts';
import './Dashboard.scss';

/** APT377: the widgets the global dashboard offers; the order inside a zone is the default order. */
export const GLOBAL_DASHBOARD_WIDGETS: WidgetDef[] = [
  { id: 'kpis', title: 'Key figures', zone: 'top', removable: false },
  { id: 'status', title: 'Accounts status', zone: 'main', removable: false },
  { id: 'monthly', title: 'Monthly submitted accounts', zone: 'main', removable: true },
  { id: 'byStatus', title: 'Accounting periods by status', zone: 'main', removable: true },
  { id: 'overdue', title: 'Accounts past their due date', zone: 'lower', removable: true },
  { id: 'activity', title: 'Recent activity', zone: 'lower', removable: true },
];

/** Where the layout (order, hidden widgets, lock) is remembered, per browser. */
export const GLOBAL_DASHBOARD_LAYOUT_KEY = 'ap.dashboard.layout.global';

interface DashboardTile {
  label: string;
  value: number;
  icon: string;
  /** Route the tile opens; empty when the tile opens its list on the dashboard instead. */
  link: string;
  /** ap5-a1: the list this figure counts, opened in place when there is no screen to go to. */
  tile?: DashboardTileKey;
  /** What the figure means, in words. */
  hint: string;
}

interface StatusRow {
  label: string;
  value: number;
}

const ACTIVITY_PERIODS = [
  { id: 0, name: 'Last 30 days' },
  { id: 1, name: 'Last week' },
  { id: 2, name: 'This month' },
  { id: 3, name: 'Last month' },
  { id: 4, name: 'This quarter' },
  { id: 5, name: 'Last quarter' },
  { id: 6, name: 'This year' },
  { id: 7, name: 'Last year' },
];
/** APT377: a handful of recent rows keeps the dashboard short; "View full log" opens the rest. */
const ACTIVITY_ROW_LIMIT = 5;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function buildTiles(summary: DashboardSummary): DashboardTile[] {
  return [
    {
      label: 'Clients',
      value: summary.totalClients,
      icon: 'assignment',
      link: '/home/manage/clients',
      hint: 'Every client on the firm. Opens the client list under Manage.',
    },
    {
      label: 'Open accounting periods',
      value: summary.openPeriods,
      icon: 'event_available',
      link: '',
      tile: 'open',
      hint: 'Accounting periods that are not yet finalised or submitted - from a period just created with no accounts through to one under review.',
    },
    {
      label: 'Accounts due in 30 days',
      value: summary.accountsDueIn30Days,
      icon: 'schedule',
      link: '',
      tile: 'due30',
      hint: 'Accounting periods whose accounts due date falls in the next 30 days.',
    },
    {
      label: 'Accounts overdue',
      value: summary.accountsOverdue,
      icon: 'report_problem',
      link: '',
      tile: 'overdue',
      hint: 'Accounting periods whose accounts due date has passed and that have not been filed.',
    },
    {
      label: 'CT600 due in 30 days',
      value: summary.ct600DueIn30Days,
      icon: 'receipt_long',
      link: '',
      tile: 'ct600',
      hint: 'Accounting periods whose CT600 due date falls in the next 30 days.',
    },
    {
      label: 'Locked periods',
      value: summary.lockedPeriods,
      icon: 'lock',
      link: '',
      tile: 'locked',
      hint: 'Accounting periods that have been locked, so their figures can no longer be changed.',
    },
    {
      label: 'Trial balances out of balance',
      value: summary.unbalancedTrialBalances,
      icon: 'balance',
      // APT377: the count opens the Accounts list filtered to the clients concerned; the detailed list stays off the dashboard.
      link: accountsLink({ outOfBalance: '1' }),
      hint: 'Trial balances whose debits and credits do not agree. Opens the Accounts list filtered to these clients.',
    },
    {
      label: 'Active users',
      value: summary.activeUsers,
      icon: 'manage_accounts',
      link: '/home/manage/users',
      hint: 'Users on the firm who can sign in. Opens the user list under Manage.',
    },
    {
      label: 'Open support tickets',
      value: summary.openSupportTickets,
      icon: 'confirmation_number',
      link: '/home/ticket',
      hint: 'Support tickets that are open or pending a reply.',
    },
  ];
}

const clientColumn = <R extends { clientName: string }>(text: (row: R) => string): DataTableColumn<R> => ({
  id: 'client',
  header: 'Client',
  cell: text,
});

/** Firm-wide dashboard: what is due, what is overdue and what needs attention (Workspace/getDashboard). */
export function Dashboard() {
  const navigate = useNavigate();

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const layout = useDashboardLayout(GLOBAL_DASHBOARD_WIDGETS, GLOBAL_DASHBOARD_LAYOUT_KEY);
  const submission = useSubmissionSummary();

  // ap5-a1: the list behind the tile the user clicked.
  const [tileList, setTileList] = useState<DashboardTileList | null>(null);
  const [activeTile, setActiveTile] = useState<DashboardTileKey | null>(null);
  const [tilePageIndex, setTilePageIndex] = useState(0);
  const [tilePageSize, setTilePageSize] = useState(10);
  const [isTileLoading, setIsTileLoading] = useState(false);
  const [revealTick, setRevealTick] = useState(0);
  const drilldownRef = useRef<HTMLDivElement>(null);
  /** The component fields Angular read at response time, mirrored so callbacks never see a stale render. */
  const tileState = useRef<{ tile: DashboardTileKey | null; pageIndex: number; pageSize: number }>({
    tile: null,
    pageIndex: 0,
    pageSize: 10,
  });

  // ap5-a1: Recent activity says what it covers and can be filtered by period and user.
  const [activity, setActivity] = useState<UserLogRow[]>([]);
  const [activityUsers, setActivityUsers] = useState<WorkspaceOption[]>([]);
  const [activityDateFilter, setActivityDateFilter] = useState<number | null>(0);
  const [activityUserId, setActivityUserId] = useState<string | null>(null);
  const [activityTotal, setActivityTotal] = useState(0);
  const activityFilters = useRef<{ dateFilter: number | null; userId: string | null }>({
    dateFilter: 0,
    userId: null,
  });

  // task-6: these lists have no server-side paging, so they are paged client-side.
  const [overduePageIndex, setOverduePageIndex] = useState(0);
  const [overduePageSize, setOverduePageSize] = useState(10);

  const loadActivity = useCallback(() => {
    const { dateFilter, userId } = activityFilters.current;
    workspaceService.getUserLogs({ pageNumber: 1, pageSize: ACTIVITY_ROW_LIMIT, dateFilter, userId }).then(
      (response) => {
        const rows = response.logList ?? [];
        setActivity(rows);
        setActivityTotal(response.pagination?.totalItemCount ?? rows.length);
      },
      () => {
        setActivity([]);
        setActivityTotal(0);
      },
    );
  }, []);

  const loadTileList = useCallback((tile: DashboardTileKey, pageIndex: number, reveal: boolean, pageSize: number) => {
    tileState.current = { tile, pageIndex, pageSize };
    setActiveTile(tile);
    setTilePageIndex(pageIndex);
    setIsTileLoading(true);
    workspaceService.getDashboardTileList({ tile, pageNumber: pageIndex + 1, pageSize }).then(
      (list) => {
        setTileList(list);
        if (reveal) {
          setRevealTick((tick) => tick + 1);
        }
        setIsTileLoading(false);
      },
      (error) => {
        setTileList(null);
        notifyError(error);
        setIsTileLoading(false);
      },
    );
  }, []);

  const load = useCallback(() => {
    workspaceService.getDashboard().then(
      (loaded) => {
        setSummary(loaded);
      },
      (error) => notifyError(error),
    );

    loadActivity();
  }, [loadActivity]);

  useEffect(() => {
    load();
    workspaceService.getUserLogOptions().then(
      (options) => setActivityUsers(options.users ?? []),
      () => setActivityUsers([]),
    );
  }, [load]);

  /** The drill-down renders below nine tiles, so bring it into view once it has rendered (also when empty). */
  useEffect(() => {
    if (revealTick === 0) {
      return;
    }
    const timer = setTimeout(() => {
      drilldownRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
    return () => clearTimeout(timer);
  }, [revealTick]);

  const tiles = useMemo(() => (summary ? buildTiles(summary) : []), [summary]);
  const overduePeriods = useMemo(() => summary?.overduePeriodList ?? [], [summary]);

  const pagedOverduePeriods = overduePeriods.slice(
    overduePageIndex * overduePageSize,
    overduePageIndex * overduePageSize + overduePageSize,
  );

  /** ap5-a1: the status breakdown also carries the due and overdue figures. */
  const periodStatusRows: StatusRow[] = (summary?.periodsByStatus ?? []).map((p) => ({
    label: p.label,
    value: p.value,
  }));
  if (summary) {
    periodStatusRows.push({ label: 'Due in the next 30 days', value: summary.accountsDueIn30Days });
    periodStatusRows.push({ label: 'Overdue', value: summary.accountsOverdue });
  }

  const activityPeriodLabel =
    activityDateFilter === null
      ? 'all time'
      : (ACTIVITY_PERIODS.find((p) => p.id === activityDateFilter)?.name?.toLowerCase() ?? 'the selected period');
  const activityUserLabel = activityUserId ? (activityUsers.find((u) => u.id === activityUserId)?.name ?? '') : '';

  /** Opens the client's accounting periods; the Accounts screens work on the selected client. */
  const openPeriods = (row: DashboardOverduePeriod) => {
    selectClient(row.clientId, row.clientName);
    void navigate('/home/taxreturns/tasks-accountingperiods');
  };

  const closeTileList = () => {
    tileState.current = { tile: null, pageIndex: 0, pageSize: tileState.current.pageSize };
    setActiveTile(null);
    setTileList(null);
    setTilePageIndex(0);
  };

  /** A tile with a screen of its own opens it; every other tile opens its list here. */
  const openTile = (tile: DashboardTile) => {
    if (tile.link) {
      void navigate(tile.link);
      return;
    }
    if (!tile.tile) {
      return;
    }
    if (activeTile === tile.tile) {
      closeTileList();
      return;
    }
    loadTileList(tile.tile, 0, true, tilePageSize);
  };

  const onTilePage = (event: { pageIndex: number; pageSize: number }) => {
    setTilePageSize(event.pageSize);
    tileState.current.pageSize = event.pageSize;
    if (activeTile) {
      // Paging within an already-open panel shouldn't re-scroll the page to it either.
      loadTileList(activeTile, event.pageIndex, false, event.pageSize);
    }
  };

  /** Opens the client behind a drill-down row on the screen the row belongs to. */
  const openTileRow = (row: DashboardTileRow) => {
    selectClient(row.clientId, row.clientName);
    void navigate(row.link);
  };

  const onActivityDateChange = (value: number | null) => {
    setActivityDateFilter(value);
    activityFilters.current.dateFilter = value;
    loadActivity();
  };

  const onActivityUserChange = (value: string | null) => {
    setActivityUserId(value);
    activityFilters.current.userId = value;
    loadActivity();
  };

  const tileColumns: DataTableColumn<DashboardTileRow>[] = [
    clientColumn((row) => `${row.clientCode} ${row.clientName}`),
    { id: 'period', header: 'Accounting period', cell: (row) => row.period },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <>
          {row.status}
          {row.isLocked && <span> (locked)</span>}
        </>
      ),
    },
    {
      id: 'due',
      header: 'Due',
      cell: (row) => (
        <>
          {row.dueDate ?? '-'}
          {row.daysOverdue > 0 && <span className="text-danger-100"> ({row.daysOverdue} days overdue)</span>}
        </>
      ),
    },
  ];

  const overdueColumns: DataTableColumn<DashboardOverduePeriod>[] = [
    clientColumn((row) => row.clientName),
    { id: 'period', header: 'Accounting period', cell: (row) => row.period },
    { id: 'due', header: 'Accounts due', cell: (row) => row.dueDate ?? '-' },
    {
      id: 'overdue',
      header: 'Days overdue',
      cell: (row) => <span className="text-danger-100">{row.daysOverdue}</span>,
    },
  ];

  const statusColumns: DataTableColumn<StatusRow>[] = [
    { id: 'label', header: 'Status', cell: (row) => row.label },
    { id: 'value', header: 'Periods', cell: (row) => row.value },
  ];

  const activityColumns: DataTableColumn<UserLogRow>[] = [
    { id: 'date', header: 'When', cell: (row) => dayjs(row.occurredAtUtc).format('DD-MMM-YYYY hh:mm A') },
    { id: 'description', header: 'Activity', cell: (row) => row.description },
    { id: 'user', header: 'User', cell: (row) => row.userName },
  ];

  const dateOptions: MatSelectOption<number>[] = [
    { value: null, label: 'All time' },
    ...ACTIVITY_PERIODS.map((period) => ({ value: period.id, label: period.name })),
  ];
  const userOptions: MatSelectOption<string>[] = [
    { value: null, label: 'All users' },
    ...activityUsers.map((user) => ({ value: user.id, label: user.name })),
  ];

  const keyFigures = (
    <div className="dash-key-figures">
      <div className="card-grid">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className={`card cursor-pointer${tile.tile && activeTile === tile.tile ? ' tile-active' : ''}`}
            title={tile.hint}
            onClick={() => openTile(tile)}
          >
            <div className="card-body">
              <div className="card-img">
                <Icon name={tile.icon} outlined />
              </div>
              <div className="card-text">
                <h2>{tile.label}</h2>
                <span>{tile.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ap5-a1: every tile without a screen of its own opens the list it counts, here. */}
      <div className="tile-drilldown mt-1" hidden={!activeTile} ref={drilldownRef}>
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <h2 className="m-0">{tileList?.title ?? 'Loading...'}</h2>
          </div>
          <div className="content-header-right mb-1">
            <Button className="action-button secondary-button" onClick={closeTileList}>
              <Icon name="close" outlined />
              <span>Close</span>
            </Button>
          </div>
        </div>
        {tileList?.description && <p className="font-size-14 mb-1">{tileList.description}</p>}

        {/* Loading state: shown while the API call is in flight */}
        {isTileLoading && (
          <div className="text-align-center p-1">
            <Icon name="refresh" outlined style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle' }} />{' '}
            Loading...
          </div>
        )}

        {tileList && tileList.rows.length > 0 && (
          <div className="mat-table-wrapper">
            <DataTable
              stickyHeader
              columns={tileColumns}
              rows={tileList.rows}
              rowKey={(row, index) => `${index}-${row.itemId}`}
              rowClassName={() => 'cursor-pointer'}
              rowTitle={() => "Open this client's accounting periods"}
              onRowClick={openTileRow}
            />
          </div>
        )}

        {tileList && tileList.rows.length === 0 && (
          <div className="text-align-center p-1">Nothing to show for this figure.</div>
        )}
        {tileList && tileList.pagination.totalItemCount > 0 && (
          <Paginator
            length={tileList.pagination.totalItemCount}
            pageSize={tilePageSize}
            pageIndex={tilePageIndex}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPage={onTilePage}
          />
        )}
      </div>
    </div>
  );

  const overdueWidget = (
    <div className="dash-list-widget">
      <div className="content-header mb-0">
        <div className="content-header-left mb-1">
          <h2 className="m-0">Accounts past their due date</h2>
        </div>
      </div>
      <div className="mat-table-wrapper mb-1">
        <DataTable
          stickyHeader
          columns={overdueColumns}
          rows={pagedOverduePeriods}
          rowKey={(row, index) => `${index}-${row.accountingPeriodId}`}
          rowClassName={() => 'cursor-pointer'}
          rowTitle={() => "Open this client's accounting periods"}
          onRowClick={openPeriods}
        />
        {overduePeriods.length === 0 && (
          <div className="text-align-center p-1">No accounting period is past its accounts due date.</div>
        )}
        {overduePeriods.length > 0 && (
          <Paginator
            length={overduePeriods.length}
            pageSize={overduePageSize}
            pageIndex={overduePageIndex}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPage={(event) => {
              setOverduePageIndex(event.pageIndex);
              setOverduePageSize(event.pageSize);
            }}
          />
        )}
      </div>
    </div>
  );

  const byStatusWidget = (
    <div className="card dash-fill">
      <div className="card-text">
        <h2 className="mb-1">Accounting periods by status</h2>
      </div>
      <div className="mat-table-wrapper no-hover">
        <DataTable
          stickyHeader
          columns={statusColumns}
          rows={periodStatusRows}
          rowKey={(row, index) => `${index}-${row.label}`}
        />
      </div>
    </div>
  );

  const activityWidget = (
    <div className="card dash-fill">
      {/* task-10: the date range and user filters sit on the title row itself - no "Filter by:" caption needed.
          The filters are a sibling of .card-text, not inside it: .card-text span is styled for the stat
          tiles' big numbers (e.g. "Clients 0"), which would otherwise bleed into the select's value text. */}
      <div className="activity-header mb-1">
        <div className="card-text">
          <h2 className="m-0">Recent activity</h2>
        </div>
        <div className="activity-filters">
          <MatSelectField
            label="Date range"
            value={activityDateFilter}
            options={dateOptions}
            onChange={onActivityDateChange}
          />
          <MatSelectField label="User" value={activityUserId} options={userOptions} onChange={onActivityUserChange} />
        </div>
      </div>

      <div className="mat-table-wrapper no-hover">
        <DataTable
          stickyHeader
          columns={activityColumns}
          rows={activity}
          rowKey={(row, index) => `${index}-${row.id}`}
        />
        {activity.length === 0 && <div className="text-align-center p-1">No activity in the selected period.</div>}
      </div>

      {/* task-10: tell the user exactly how many entries are shown, for which period, and for which user. */}
      <div className="d-flex align-items-center justify-space-between mt-1">
        <span className="font-size-14 text-gray">
          Showing <strong>{activity.length}</strong> of <strong>{activityTotal}</strong> entries for{' '}
          <strong>{activityPeriodLabel}</strong>
          {activityUserLabel && (
            <>
              {' '}
              — <strong>{activityUserLabel}</strong>
            </>
          )}
          .
        </span>
        <Link className="link text-underline font-size-14" to="/home/taxreturns/userlogs">
          View full log
        </Link>
      </div>
    </div>
  );

  const widgetContent: Record<string, ReactNode> = {
    kpis: keyFigures,
    status: <AccountsStatusWidget summary={submission.summary} failed={submission.failed} />,
    monthly: (
      <MonthlySubmittedWidget summary={submission.summary} failed={submission.failed} onYear={submission.load} />
    ),
    byStatus: byStatusWidget,
    overdue: overdueWidget,
    activity: activityWidget,
  };

  return (
    <div className="form wp-100 dashboard-page">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <h2 className="m-0">Accounts Production</h2>
          </div>
          <div className="content-header-right mb-1">
            <DashboardToolbar layout={layout} />
          </div>
        </div>

        {/* APT377: the "n accounting periods are past the accounts due date" banner is gone - the Overdue tile and the list below already show it.
            The detailed list of trial balances that do not balance is gone too: its tile is a link to the Accounts list filtered to them. */}
        <DashboardZones
          layout={layout}
          wideIds={['kpis']}
          zoneClassName={{ top: 'dash-zone--top', main: 'dash-zone--main', lower: 'dash-zone--lower' }}
          renderWidget={(widget) => widgetContent[widget.id] ?? null}
        />
      </div>
    </div>
  );
}
